/**
 * Follow-Up Section Edit Page
 *
 * Focused edit view for a single section from the Review page.
 * URL: /update/:moduleId
 *
 * - Shows only ONE section's fields
 * - No stepper, no "1 of 13" counter
 * - Header: section name only
 * - Cancel → back to review (no changes)
 * - Save changes → persist + back to review with toast
 */

import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useFollowUp } from '../context/FollowUpContext';
import { useTenant } from '../context/TenantContext';
import DynamicStepComponent from '../components/form/DynamicStepComponent';
import Medication from '../components/medication/Medication';
import Pharmacy from '../components/Pharmacy/Pharmacy';
import MedicationAllergies from '../components/MedicationAllergies/MedicationAllergies';
import Step5Diagram from '../components/form/Step5Diagram';
import { generateYupSchema } from '../utils/validationSchemas';
import { registerFieldLabels } from '../utils/fieldLabelRegistry';
import { DEFAULT_STATE } from '../utils/usStates';
import { environment } from '../env/environment';
import defaultLogo from '../assets/logo.png';
import logger from '../utils/logger';
import activityLogger from '../utils/activityLogger';
import '../styles/formPage.css';
import '../styles/followup.css';
import * as Yup from 'yup';

const convertFieldNameToLowercase = (fieldName) => {
  if (!fieldName) return '';
  return fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
};

const getFieldName = (field, index) => {
  if (!field) return `field_${index}`;
  if (field.name) return convertFieldNameToLowercase(field.name);
  if (field.labelName) return convertFieldNameToLowercase(field.labelName);
  if (field.label) return convertFieldNameToLowercase(field.label);
  return `field_${index}`;
};

// Section edits happen from the review page, so they log under its step.
const REVIEW_STEP_NUMBER = 4;

const FollowUpSectionEdit = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const {
    allModules,
    fieldsMap,
    previousData,
    updatedFields,
    updateFields,
    getMergedFormData,
    isExistingUser,
  } = useFollowUp();

  const [moduleFields, setModuleFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const step5Ref = useRef(null);
  const logoToDisplay = tenant?.logoUrl || defaultLogo;

  // Find the matching module
  const currentModule = useMemo(() => {
    return allModules.find(mod => {
      const id = (mod.module_name || '').toLowerCase().replace(/\s+/g, '_');
      return id === moduleId;
    });
  }, [allModules, moduleId]);

  const moduleName = currentModule?.module_name || '';
  const moduleType = currentModule?.module_type || '';
  const isPainDiagram = moduleType === 'pain_diagram' || moduleName.toLowerCase() === 'pain diagram';

  // Get fields for this module
  useEffect(() => {
    const fetchFields = async () => {
      // Try from context fieldsMap first
      if (fieldsMap[moduleName]?.length > 0) {
        registerFieldLabels(fieldsMap[moduleName]);
        setModuleFields(fieldsMap[moduleName]);
        setLoading(false);
        return;
      }

      // Fetch from API if not in context
      if (moduleName && !isPainDiagram) {
        try {
          const res = await fetch(`${environment.baseUrl}/api/followup-modules/getFields/${moduleName}`);
          const data = await res.json();
          const fields = data.fields || [];
          registerFieldLabels(fields);
          setModuleFields(fields);
        } catch (err) {
          logger.error('Error fetching fields:', err?.message);
        }
      }
      setLoading(false);
    };

    if (currentModule) fetchFields();
    else setLoading(false);
  }, [currentModule, moduleName, fieldsMap, isPainDiagram]);

  // Build initial values from merged form data
  // Replicates the same complex restoration logic from FollowUpSteps.jsx
  const initialValues = useMemo(() => {
    const mergedData = getMergedFormData();
    const values = {};

    // Copy ALL existing merged data with both original and normalized keys
    Object.keys(mergedData).forEach(key => {
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      values[normalizedKey] = mergedData[key];
      values[key] = mergedData[key];
    });

    // Helper: find value by various key formats
    const findValueInData = (data, baseFieldName) => {
      if (!data || !baseFieldName) return undefined;
      const normalized = baseFieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (data[baseFieldName] !== undefined && data[baseFieldName] !== '') return data[baseFieldName];
      if (data[normalized] !== undefined && data[normalized] !== '') return data[normalized];
      const withUnderscore = baseFieldName.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
      if (data[withUnderscore] !== undefined && data[withUnderscore] !== '') return data[withUnderscore];
      for (const dataKey of Object.keys(data)) {
        if (dataKey.toLowerCase().replace(/[^a-z0-9]/g, '') === normalized && data[dataKey] !== undefined && data[dataKey] !== '') return data[dataKey];
      }
      return undefined;
    };

    // Restore address fields
    const restoreAddressFields = (baseFieldName) => {
      ['street', 'suite', 'city', 'state', 'country'].forEach(part => {
        const formats = [`${baseFieldName}_${part}`, `${baseFieldName}${part}`, `${baseFieldName.toLowerCase()}_${part}`, `${baseFieldName.toLowerCase()}${part}`];
        for (const format of formats) {
          const val = findValueInData(mergedData, format);
          if (val !== undefined && val !== '') {
            values[`${baseFieldName}_${part}`] = val;
            values[`${baseFieldName}${part}`] = val;
            break;
          }
        }
      });
    };

    // Restore authorized contacts from flattened keys back to array
    const restoreAuthorizedContacts = (fieldName) => {
      // Check array format first (from previous section edit saves in updatedFields)
      const arrayKey = fieldName || 'authorizedcontacts';
      const contactArray = mergedData[arrayKey] || mergedData.authorizedcontacts || mergedData.authorizedContacts;
      if (Array.isArray(contactArray) && contactArray.length > 0 && contactArray[0]?.firstName !== undefined) {
        return contactArray.map((c, i) => {
          let parsedRelationship = c.relationship || '';
          let parsedOtherRelationship = c.otherRelationship || '';
          if (parsedRelationship.startsWith('Other - ')) {
            parsedOtherRelationship = parsedRelationship.substring(8);
            parsedRelationship = 'Other';
          }
          return {
            id: c.id || Date.now() + i,
            firstName: c.firstName || '',
            lastName: c.lastName || '',
            email: c.email || '',
            phone: c.phone || '',
            relationship: parsedRelationship,
            otherRelationship: parsedOtherRelationship,
            isEmergencyContact: c.isEmergencyContact === true || c.isEmergencyContact === 'Yes',
            isAuthorizedMedical: c.isAuthorizedMedical === true || c.isAuthorizedMedical === 'Yes',
          };
        });
      }

      // Fallback: read from flattened keys (from backend/previousData)
      const contacts = [];
      for (let i = 1; i <= 10; i++) {
        const prefix = `authorizedcontact${i}`;
        const firstName = findValueInData(mergedData, `${prefix}firstname`);
        const lastName = findValueInData(mergedData, `${prefix}lastname`);
        const email = findValueInData(mergedData, `${prefix}email`);
        const phone = findValueInData(mergedData, `${prefix}phone`);
        const relationship = findValueInData(mergedData, `${prefix}relationship`);
        const isEmergency = findValueInData(mergedData, `${prefix}isemergency`);
        const isMedicalAuth = findValueInData(mergedData, `${prefix}ismedicalauth`);

        if (firstName || lastName || email || phone) {
          let parsedRelationship = relationship || '';
          let parsedOtherRelationship = '';
          if (parsedRelationship.startsWith('Other - ')) {
            parsedOtherRelationship = parsedRelationship.substring(8);
            parsedRelationship = 'Other';
          }
          contacts.push({
            id: Date.now() + i,
            firstName: firstName || '',
            lastName: lastName || '',
            email: email || '',
            phone: phone || '',
            relationship: parsedRelationship,
            otherRelationship: parsedOtherRelationship,
            isEmergencyContact: isEmergency === 'Yes' || isEmergency === true || isEmergency === 'true',
            isAuthorizedMedical: isMedicalAuth === 'Yes' || isMedicalAuth === true || isMedicalAuth === 'true',
          });
        }
      }
      return contacts.length > 0 ? contacts : null;
    };

    // Restore accordion checkboxes
    const restoreAccordionCheckboxes = (field, fieldName) => {
      if (!field.accordionSections) return;
      field.accordionSections.forEach((section, sectionIndex) => {
        const sectionFieldName = `${fieldName}_section_${sectionIndex}`;

        // Check raw checkbox keys first (from previous section edit saves in updatedFields)
        const hasRawKeys = section.checkboxes?.some((_, ci) => {
          const ck = `${sectionFieldName}_checkbox_${ci}`;
          return mergedData[ck] !== undefined;
        });

        if (hasRawKeys) {
          // Restore from raw checkbox keys (freshest data from previous edits)
          section.checkboxes?.forEach((checkbox, checkboxIndex) => {
            const checkboxFieldName = `${sectionFieldName}_checkbox_${checkboxIndex}`;
            values[checkboxFieldName] = mergedData[checkboxFieldName] === true || mergedData[checkboxFieldName] === 'true';
          });
        } else {
          // Fallback: restore from processed format (from backend/previousData)
          const sectionKey = `${fieldName}_${section.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
          const sectionKeyNormalized = sectionKey.replace(/_/g, '');
          let storedSection = findValueInData(mergedData, sectionKey) || findValueInData(mergedData, sectionKeyNormalized);

          if (storedSection?.selectedValues && Array.isArray(storedSection.selectedValues)) {
            section.checkboxes?.forEach((checkbox, checkboxIndex) => {
              const checkboxFieldName = `${sectionFieldName}_checkbox_${checkboxIndex}`;
              values[checkboxFieldName] = storedSection.selectedValues.includes(checkbox.label);
            });
          }
        }
      });
    };

    // Restore conditional field values
    const restoreConditionalFields = (field, fieldName) => {
      if (!field.optionsWithConditional) return;
      const mainValue = findValueInData(mergedData, fieldName);
      if (!mainValue) return;
      field.optionsWithConditional.forEach((config, optionIndex) => {
        if (!config.conditionalInputs) return;
        const optionValue = config.optionValue || config.label;
        if (mainValue === optionValue || (typeof mainValue === 'string' && mainValue.startsWith(`${optionValue} - `))) {
          config.conditionalInputs.forEach((conditionalInput, conditionalIndex) => {
            const conditionalFieldName = `${fieldName}_conditional_${optionIndex}_${conditionalIndex}`;
            if (typeof mainValue === 'string' && mainValue.includes(' - ')) {
              const parts = mainValue.split(' - ');
              if (parts.length > 1) values[conditionalFieldName] = parts.slice(1).join(' - ');
            }
            const directValue = findValueInData(mergedData, conditionalFieldName);
            if (directValue) values[conditionalFieldName] = directValue;
          });
        }
      });
    };

    // Process each field in this module
    moduleFields.forEach((field, index) => {
      const fieldName = getFieldName(field, index);

      // Address fields
      if (field.type === 'address') {
        restoreAddressFields(fieldName);
        if (!values[`${fieldName}_street`] && !values[`${fieldName}street`]) values[`${fieldName}_street`] = '';
        if (!values[`${fieldName}_suite`] && !values[`${fieldName}suite`]) values[`${fieldName}_suite`] = '';
        if (!values[`${fieldName}_city`] && !values[`${fieldName}city`]) values[`${fieldName}_city`] = '';
        if (!values[`${fieldName}_state`] && !values[`${fieldName}state`]) values[`${fieldName}_state`] = DEFAULT_STATE;
        if (!values[`${fieldName}_country`] && !values[`${fieldName}country`]) values[`${fieldName}_country`] = 'US';
        return;
      }

      // Authorized contacts
      if (field.type === 'authorized_contacts') {
        const restoredContacts = restoreAuthorizedContacts(fieldName);
        if (restoredContacts && restoredContacts.length > 0) {
          values[fieldName] = restoredContacts;
        } else if (!values[fieldName] || !Array.isArray(values[fieldName]) || values[fieldName].length === 0) {
          values[fieldName] = [{
            id: Date.now(), firstName: '', lastName: '', email: '', phone: '',
            relationship: '', otherRelationship: '', isEmergencyContact: false, isAuthorizedMedical: false,
          }];
        }
        return;
      }

      // Accordion checkboxes
      if (field.type === 'accordion') {
        restoreAccordionCheckboxes(field, fieldName);
        return;
      }

      // Conditional radio buttons
      if (field.type === 'radio' && field.optionsWithConditional) {
        restoreConditionalFields(field, fieldName);
      }

      // Find existing value
      let existingValue = values[fieldName];
      if (existingValue === undefined || existingValue === '') existingValue = findValueInData(mergedData, fieldName);
      if (existingValue === undefined || existingValue === '') existingValue = findValueInData(mergedData, field.name);
      if (existingValue === undefined || existingValue === '') existingValue = findValueInData(mergedData, field.labelName);
      if (existingValue === undefined || existingValue === '') existingValue = findValueInData(mergedData, field.label);

      if (existingValue !== undefined && existingValue !== '') {
        values[fieldName] = existingValue;
        return;
      }

      // Set defaults only if no value found
      switch (field.type) {
        case 'checkbox': values[fieldName] = field.options?.length > 0 ? [] : false; break;
        case 'multiselect': values[fieldName] = []; break;
        case 'number': case 'range': values[fieldName] = ''; break;
        default: values[fieldName] = ''; break;
      }
    });

    // Medication data - full structure restoration
    const existingMedData = mergedData.medicationdata || mergedData.medicationData || mergedData.MedicationData || values['medicationdata'];
    if (existingMedData && typeof existingMedData === 'object') {
      values['medicationdata'] = existingMedData;
    } else {
      values['medicationdata'] = {
        uploadedFiles: [], cameraFiles: [],
        extractedText: {}, extractedTextFromFiles: {}, extractedTextFromCamera: {},
        extractedMedicationsFormatted: '', extractedMedicationsCount: 0,
        searchResults: [], searchResultsFormatted: '', searchResultsCount: 0,
        wizardSelections: [], wizardSelectionsFormatted: '', wizardSelectionsCount: 0,
        VoiceInputResults: [], voiceInputFormatted: '', voiceInputCount: 0,
        selectedPharmacy: null, pharmacyFormatted: '',
        totalMedicationsCount: 0, timestamp: null,
      };
    }

    // Allergies data - full structure restoration
    const existingAllergyData = mergedData.allergiesdata || mergedData.allergiesData || mergedData.AllergiesData || values['allergiesdata'];
    if (existingAllergyData && typeof existingAllergyData === 'object') {
      values['allergiesdata'] = existingAllergyData;
    } else {
      values['allergiesdata'] = {
        uploadedFiles: [], extractedTextFromFiles: {},
        VoiceInputResults: [], totalAllergiesCount: 0, timestamp: null,
      };
    }

    // Pharmacy data - full structure restoration
    const existingPharmacyData = mergedData.pharmacydata || mergedData.pharmacyData || mergedData.PharmacyData || values['pharmacydata'];
    if (existingPharmacyData && typeof existingPharmacyData === 'object') {
      values['pharmacydata'] = existingPharmacyData;
    } else {
      values['pharmacydata'] = { selectedPharmacy: null, pharmacyFormatted: '' };
    }

    // Also set pharmacyformatted if exists
    if (existingPharmacyData?.pharmacyFormatted) {
      values['pharmacyformatted'] = existingPharmacyData.pharmacyFormatted;
    }

    // Manual medications - check both camelCase and lowercase
    const manualMeds = mergedData.manualMedications || mergedData.manualmedications || values['manualMedications'] || values['manualmedications'];
    values['manualMedications'] = Array.isArray(manualMeds) ? manualMeds : [];

    const manualAlrg = mergedData.manualAllergies || mergedData.manualallergies || values['manualAllergies'] || values['manualallergies'];
    values['manualAllergies'] = Array.isArray(manualAlrg) ? manualAlrg : [];

    // Pain levels
    if (values['currentpainlevel'] === undefined) values['currentpainlevel'] = '';
    if (values['worstpainlevel'] === undefined) values['worstpainlevel'] = '';
    if (values['bestpainlevel'] === undefined) values['bestpainlevel'] = '';

    return values;
  }, [moduleFields, getMergedFormData]);

  // Validation schema
  const validationSchema = useMemo(() => {
    if (isPainDiagram || moduleFields.length === 0) return Yup.object({});
    return generateYupSchema(moduleFields);
  }, [moduleFields, isPainDiagram]);

  // Save only changed fields (compare current values vs initialValues)
  const handleSave = (values) => {
    const changedOnly = {};
    const trivialValues = new Set(['', '-', 'N/A', 'n/a', 'None', 'none', 'US']);

    Object.keys(values).forEach(key => {
      const cur = values[key];
      const init = initialValues[key];
      const keyLC = key.toLowerCase();

      // Always include complex module data keys
      if (key === 'medicationdata' || key === 'allergiesdata' || key === 'pharmacydata' ||
          key === 'manualMedications' || key === 'manualAllergies' || key === 'pharmacyformatted' ||
          key === 'manualmedicationlist' || key === 'manualallergieslist' ||
          key === 'medicationlistfromfiles' || key === 'medicationlistfromcamera' ||
          key === 'medicationvoiceinputlist' || key === 'medicationlistfromwizard' ||
          key === 'allergieslistfromfiles' || key === 'allergiesvoiceinputlist') {
        changedOnly[key] = cur;
        return;
      }

      // Always include ALL accordion checkbox keys (review page needs complete set)
      if (keyLC.includes('_section_') && keyLC.includes('_checkbox_')) {
        changedOnly[key] = cur;
        return;
      }

      // For arrays (authorized contacts), compare by JSON
      if (Array.isArray(cur) || Array.isArray(init)) {
        if (JSON.stringify(cur) !== JSON.stringify(init)) {
          changedOnly[key] = cur;
        }
        return;
      }

      // For objects, compare by JSON
      if (typeof cur === 'object' && cur !== null) {
        if (JSON.stringify(cur) !== JSON.stringify(init)) {
          changedOnly[key] = cur;
        }
        return;
      }

      // For primitives: skip if both are trivial/empty, only save if actually different
      const curStr = String(cur ?? '').trim();
      const initStr = String(init ?? '').trim();
      if (curStr !== initStr) {
        // Skip if init was empty and current is just a trivial default
        if (!initStr && trivialValues.has(curStr)) return;
        changedOnly[key] = cur;
      }
    });

    updateFields(changedOnly);
    activityLogger.stepCompleted(REVIEW_STEP_NUMBER, `Updated ${moduleName}`);
    navigate('/review', { state: { toast: `${moduleName} updated` } });
  };

  // Cancel - go back without saving
  const handleCancel = () => {
    navigate('/review');
  };

  // Pain diagram submit
  const handlePainDiagramSubmit = (diagramPdf) => {
    updateFields({ painDiagramPdf: diagramPdf });
    activityLogger.stepCompleted(REVIEW_STEP_NUMBER, 'Updated Pain Diagram');
    navigate('/review', { state: { toast: 'Pain Diagram updated' } });
  };

  // Render the correct component based on module type
  const renderModule = (props) => {
    const { values, errors, touched, setFieldValue } = props;

    if (isPainDiagram) {
      return (
        <Step5Diagram
          ref={step5Ref}
          onSubmitWithPopup={handlePainDiagramSubmit}
          canvasGender={values.canvasGender || 'woman'}
          setCanvasGender={() => {}}
          previousDrawings={isExistingUser ? (previousData?.painDiagramData || previousData?.paindiagramdata) : null}
        />
      );
    }

    if (moduleType === 'medication') {
      return <Medication moduleData={moduleFields} {...props} />;
    }

    if (moduleType === 'medication_allergies') {
      return <MedicationAllergies moduleData={moduleFields} {...props} />;
    }

    if (moduleType === 'pharmacy') {
      return <Pharmacy moduleData={moduleFields} {...props} />;
    }

    return (
      <DynamicStepComponent
        {...props}
        step={1}
        moduleName={moduleName}
        moduleFields={moduleFields}
        moduleData={currentModule}
        getFieldName={getFieldName}
        isFollowUpUpdate={true}
      />
    );
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="followup-spinner"></div>
      </div>
    );
  }

  if (!currentModule) {
    // EPH-97: Redirect to / instead of showing "Section not found"
    navigate('/', { replace: true });
    return null;
  }

  return (
    <div>
      {/* Logo */}
      {!isPainDiagram && (
        <div className="logo-img d-flex-center">
          <div style={{ textAlign: 'center' }}>
            <img src={logoToDisplay} alt={tenant?.brandName || 'Logo'} style={{ width: '200px', height: 'auto', objectFit: 'contain', marginTop: '10px' }} />
          </div>
        </div>
      )}

      {/* Back to review breadcrumb */}
      <div style={{ maxWidth: '800px', margin: '10px auto', padding: '0 15px' }}>
        <button
          onClick={handleCancel}
          style={{
            background: 'none', border: 'none', color: '#2486c5', fontSize: '14px',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to review
        </button>
      </div>

      {/* Section name header — update-specific copy */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 15px 10px', textAlign: 'center' }}>
        <h2 style={{
          fontSize: '18px', fontWeight: '500', color: '#1f5b8a',
          margin: '0 0 4px',
        }}>
          Update {moduleName}
        </h2>
        <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>
          Review and update your information below. Save changes when done.
        </p>
      </div>

      {/* Form */}
      <div className="form-container wide">
        <Formik
          key={`section-${moduleId}-${moduleFields.length}-${loading}`}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => handleSave(values)}
          enableReinitialize={false}
        >
          {({ values, errors, touched, setFieldValue, submitForm }) => (
            <Form onSubmit={(e) => e.preventDefault()} autoComplete="off">
              {renderModule({ values, errors, touched, setFieldValue })}

              {/* Bottom nav: Cancel + Save changes */}
              {!isPainDiagram && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', gap: '12px',
                  marginTop: '24px', padding: '16px 0',
                }}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    style={{
                      padding: '12px 24px', fontSize: '14px', fontWeight: '500',
                      border: '1px solid #c2cad3', background: '#fff', color: '#333',
                      borderRadius: '8px', cursor: 'pointer', flex: 1,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submitForm}
                    style={{
                      padding: '12px 24px', fontSize: '14px', fontWeight: '500',
                      border: 'none', background: 'var(--color-primary, #4a7a2c)', color: '#fff',
                      borderRadius: '8px', cursor: 'pointer', flex: 1,
                    }}
                  >
                    Save changes
                  </button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default FollowUpSectionEdit;
